/**
 * Collection article spreads: ambient video playback and the full-size image
 * viewer (lightbox).
 *
 * Ambient videos (muted, looped, no controls) play only while at least 25% in
 * view and pause when out of view. A 44px toggle lets the visitor pause/play.
 * With prefers-reduced-motion: reduce nothing autoplays; the poster shows with
 * the play button. Film videos (with sound) use native controls and need no JS.
 *
 * Initialised per section root ([data-spreads-video]); each root is wired once.
 */
(function () {
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function initVideo(video, reduce) {
    var wrap = video.closest('.wallace-spread__image');
    var button = wrap ? wrap.querySelector('[data-spread-toggle]') : null;
    if (!button) return null;
    var iconPause = button.querySelector('[data-icon-pause]');
    var iconPlay = button.querySelector('[data-icon-play]');
    var state = { userPaused: reduce(), inView: false, observer: null };

    // SVG elements have no reliable `hidden` property, so toggle the attribute.
    function setHidden(el, hidden) {
      if (!el) return;
      if (hidden) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    // Driven only by the video's own events, never by the click handler.
    function render() {
      var playing = !video.paused && !video.ended;
      button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      setHidden(iconPause, !playing);
      setHidden(iconPlay, playing);
    }

    function play() {
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () { render(); });
    }

    button.addEventListener('click', function () {
      if (video.paused) {
        state.userPaused = false;
        play();
      } else {
        state.userPaused = true;
        video.pause();
      }
    });

    video.addEventListener('play', render);
    video.addEventListener('playing', render);
    video.addEventListener('pause', render);
    video.addEventListener('ended', render);

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          state.inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          if (state.inView) {
            if (!state.userPaused) play();
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { threshold: [0, 0.25] });
      state.observer.observe(video);
    }

    render();
    return state;
  }

  /* ---------- Full-size image viewer (lightbox) ---------- */
  // Mirrors the editorial lightbox (section-article-looks.js): desktop fits the
  // image to the width and scrolls inside the overlay, mobile fits on white with
  // swipe. Prev/next walks every image in the post in page order.
  function initLightbox(root) {
    var overlay = root.querySelector('[data-spreads-overlay]');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('img[data-spreads-lightbox-img]'));
    if (!overlay || triggers.length === 0) return;

    // Move the overlay to <body> so its z-index (10000) is root-level: above the
    // sticky header, below the Shop-the-Look drawer (10001). Drop a stale copy
    // left over from a previous render of this section (theme editor reloads).
    var overlayId = root.getAttribute('data-section-id') || '';
    overlay.setAttribute('data-spreads-overlay-id', overlayId);
    Array.prototype.forEach.call(document.body.querySelectorAll('[data-spreads-overlay-id="' + overlayId + '"]'), function (old) {
      if (old !== overlay) old.parentNode.removeChild(old);
    });
    if (overlay.parentNode !== document.body) document.body.appendChild(overlay);

    var imgEl = overlay.querySelector('[data-spreads-img]');
    var prevBtn = overlay.querySelector('[data-spreads-prev]');
    var nextBtn = overlay.querySelector('[data-spreads-next]');
    var closeBtn = overlay.querySelector('[data-spreads-close]');
    var bagBtn = overlay.querySelector('[data-spreads-bag]');
    var dotsWrap = overlay.querySelector('[data-spreads-dots]');

    var items = triggers.map(function (t) {
      return {
        src: t.getAttribute('data-full') || t.currentSrc || t.src,
        alt: t.getAttribute('alt') || '',
        focal: t.getAttribute('data-focal') || '50% 50%',
        // The spread's own bag opener (shared Shop-the-Look drawer), if shoppable.
        opener: t.parentNode ? t.parentNode.querySelector('[data-wallace-look-open]') : null,
      };
    });

    var current = 0;
    var lastFocused = null;
    var multiple = items.length > 1;
    var coverMedia = window.matchMedia('(min-width: 768px)');
    var prevBodyOverflow = '';

    if (!multiple) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
    }

    var dots = [];
    if (dotsWrap) {
      if (multiple) {
        items.forEach(function (item, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'wallace-spreads-lightbox__dot';
          dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
          dot.addEventListener('click', function () {
            current = index;
            render();
            positionScroll();
          });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      } else {
        dotsWrap.hidden = true;
      }
    }

    function drawerOpen() {
      return !!document.querySelector('.wallace-look-drawer.is-open');
    }

    // The full-size image is only requested here, when the viewer shows it.
    function render() {
      var item = items[current];
      if (!item) return;
      imgEl.setAttribute('src', item.src);
      imgEl.setAttribute('alt', item.alt);
      if (bagBtn) bagBtn.hidden = !item.opener;
      dots.forEach(function (dot, index) {
        dot.classList.toggle('is-active', index === current);
      });
    }

    function scrollToFocal() {
      var item = items[current];
      var focalY = 50;
      if (item && item.focal) {
        var parts = item.focal.split(/\s+/);
        var parsed = parts.length === 2 ? parseFloat(parts[1]) : NaN;
        if (!isNaN(parsed)) focalY = parsed;
      }
      var imgHeight = imgEl.offsetHeight || imgEl.naturalHeight || 0;
      var viewportHeight = overlay.clientHeight || window.innerHeight;
      var target = (focalY / 100) * imgHeight - viewportHeight / 2;
      var max = Math.max(0, imgHeight - viewportHeight);
      overlay.scrollTop = Math.min(Math.max(0, target), max);
    }

    function positionScroll() {
      imgEl.onload = null;
      if (coverMedia.matches) {
        if (imgEl.complete && imgEl.naturalHeight) {
          scrollToFocal();
        } else {
          imgEl.onload = scrollToFocal;
        }
      } else {
        overlay.scrollTop = 0;
      }
    }

    function getFocusable() {
      return Array.prototype.slice.call(overlay.querySelectorAll('button')).filter(function (el) {
        return !el.hidden && !el.disabled && el.offsetParent !== null;
      });
    }

    function isOpen() {
      return !overlay.hidden;
    }

    function open(index) {
      current = index;
      lastFocused = triggers[index];
      render();
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      positionScroll();
      document.addEventListener('keydown', onKeydown);
      (closeBtn || overlay).focus();
    }

    function close() {
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = prevBodyOverflow;
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function step(delta) {
      if (!multiple) return;
      current = (current + delta + items.length) % items.length;
      render();
      positionScroll();
    }

    function onKeydown(event) {
      // While the Shop-the-Look drawer is open over the viewer, it owns the keys.
      if (!isOpen() || drawerOpen()) return;
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowLeft') {
        step(-1);
      } else if (event.key === 'ArrowRight') {
        step(1);
      } else if (event.key === 'Tab') {
        // Trap focus inside the viewer.
        var focusable = getFocusable();
        if (focusable.length === 0) {
          event.preventDefault();
          overlay.focus();
          return;
        }
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var active = document.activeElement;
        if (!overlay.contains(active)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    function onBreakpointChange() {
      if (!isOpen()) return;
      render();
      positionScroll();
    }
    if (coverMedia.addEventListener) {
      coverMedia.addEventListener('change', onBreakpointChange);
    } else if (coverMedia.addListener) {
      coverMedia.addListener(onBreakpointChange);
    }

    // Images act as buttons: click, Enter or Space opens the viewer.
    triggers.forEach(function (trigger, index) {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
      var label = trigger.getAttribute('alt');
      trigger.setAttribute('aria-label', label ? 'View full size: ' + label : 'View image full size');
      trigger.addEventListener('click', function () {
        open(index);
      });
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          open(index);
        }
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    // Bag: trigger the spread's own opener so the shared drawer opens above the
    // viewer (z-index 10001 vs 10000). The drawer returns focus to this button
    // when it closes, and the viewer stays open underneath.
    if (bagBtn) {
      bagBtn.addEventListener('click', function () {
        var item = items[current];
        if (item && item.opener) item.opener.click();
      });
    }

    // Backdrop click closes (not the image or a control).
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });

    // Touch swipe left/right between images (mobile).
    var touchStartX = 0;
    var touchStartY = 0;
    var touchActive = false;
    overlay.addEventListener('touchstart', function (event) {
      if (drawerOpen() || event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
      touchActive = true;
    }, { passive: true });
    overlay.addEventListener('touchend', function (event) {
      if (!touchActive) return;
      touchActive = false;
      var touch = event.changedTouches[0];
      var dx = touch.clientX - touchStartX;
      var dy = touch.clientY - touchStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        step(dx < 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  function initRoot(root) {
    if (!root || root.dataset.spreadsVideoReady === '1') return;
    root.dataset.spreadsVideoReady = '1';

    if (root.hasAttribute('data-spreads-lightbox')) initLightbox(root);

    var reduce = function () { return !!(reduceQuery && reduceQuery.matches); };
    var states = [];
    Array.prototype.forEach.call(root.querySelectorAll('video[data-spread-ambient]'), function (video) {
      var state = initVideo(video, reduce);
      if (state) states.push({ video: video, state: state });
    });

    // If the visitor turns reduced motion on, stop playback and keep the poster.
    if (reduceQuery && reduceQuery.addEventListener) {
      reduceQuery.addEventListener('change', function () {
        if (!reduce()) return;
        states.forEach(function (item) {
          item.state.userPaused = true;
          item.video.pause();
        });
      });
    }
  }

  function scan(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-spreads-video], [data-spreads-lightbox]'), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  // Theme editor: drop a removed section's overlay (it lives in <body>).
  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-spreads-lightbox]'), function (root) {
      var id = root.getAttribute('data-section-id') || '';
      Array.prototype.forEach.call(document.body.querySelectorAll('[data-spreads-overlay-id="' + id + '"]'), function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    });
  });

  // Theme editor: wire a section only when it is (re)loaded.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
